import imageCompression from "browser-image-compression";

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_FILE_SIZE_DISPLAY = "10MB";

const VIDEO_EXTENSIONS = [
  ".mp4",
  ".mov",
  ".avi",
  ".webm",
  ".mkv",
  ".m4v",
  ".3gp",
];

export function isVideoFile(
  file: File | { name?: string; type?: string },
): boolean {
  if (file instanceof File && file.type.startsWith("video/")) return true;
  const name = ("name" in file ? file.name : "") || "";
  return VIDEO_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));
}

export function isVideoUrl(url: string): boolean {
  if (!url) return false;
  // Strip query params for extension check
  const path = url.split("?")[0].toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => path.endsWith(ext));
}

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".bmp",
  ".heic",
  ".heif",
  ".tiff",
  ".tif",
  ".avif",
];

/** Best-effort check that a URL/path/filename points at an image, by
 * extension. Used to gate image-only actions on attachments that may also be
 * videos, PDFs, or other documents. */
export function isImageUrl(url: string): boolean {
  if (!url) return false;
  const path = url.split("?")[0].toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
  /** Force the output image type (e.g. "image/jpeg"). Defaults to the input type. */
  fileType?: string;
}

export const GALLERY_IMAGE_COMPRESSION_OPTIONS: CompressionOptions = {
  maxSizeMB: 8,
  maxWidthOrHeight: 4096,
  initialQuality: 0.92,
};

export interface CompressionResult {
  file: File;
  originalName: string;
  wasCompressed: boolean;
  originalSize: number;
  compressedSize: number;
}

function isHeicFile(file: File): boolean {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif")
  );
}

/**
 * Convert a HEIC/HEIF file to JPEG for browser compatibility.
 */
async function convertHeicToJpeg(file: File): Promise<File> {
  const { default: heic2any } = await import("heic2any");
  const blob = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });
  const resultBlob = Array.isArray(blob) ? blob[0] : blob;
  const jpegName = file.name
    .replace(/\.heic$/i, ".jpg")
    .replace(/\.heif$/i, ".jpg");
  return new File([resultBlob], jpegName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/**
 * Compress an image file if it exceeds the maximum size or is larger than optimal.
 * Returns the compressed file or original if compression is not needed/possible.
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {},
): Promise<CompressionResult> {
  const {
    maxSizeMB = 2, // Target 2MB for faster uploads
    maxWidthOrHeight = 2048,
    initialQuality = 0.85,
    fileType,
  } = options;

  const originalSize = file.size;
  const originalName = file.name;

  // Skip non-image files
  if (!file.type.startsWith("image/") && !isHeicFile(file)) {
    return {
      file,
      originalName,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize,
    };
  }

  // Convert HEIC/HEIF to JPEG first, then compress
  let processedFile = file;
  if (isHeicFile(file)) {
    try {
      processedFile = await convertHeicToJpeg(file);
    } catch (error) {
      console.error("HEIC conversion failed:", error);
      return {
        file,
        originalName,
        wasCompressed: false,
        originalSize,
        compressedSize: originalSize,
      };
    }
  }

  // Always try to compress images to improve upload speed
  // Even smaller images can benefit from slight compression
  try {
    const compressedBlob = await imageCompression(processedFile, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      initialQuality,
      ...(fileType ? { fileType } : {}),
    });

    // Use the compressed version when it's actually smaller, or whenever the
    // caller forced a `fileType` — in that case they want the re-encode (a
    // specific format and the size/dimension target) even if the original
    // happened to be smaller, so the output type is guaranteed.
    if (compressedBlob.size < processedFile.size || fileType) {
      // When a `fileType` was forced, rewrite the filename extension to match
      // so the name and MIME type stay consistent (e.g. a forced JPEG of
      // "scan.png" becomes "scan.jpg"); otherwise preserve the original name.
      const outputName = fileType
        ? processedFile.name.replace(/\.[^./\\]+$/, "") +
          `.${fileType.split("/")[1]?.replace("jpeg", "jpg") || "jpg"}`
        : processedFile.name;
      // Convert Blob back to File
      const compressedFile = new File([compressedBlob], outputName, {
        type: compressedBlob.type,
        lastModified: Date.now(),
      });

      return {
        file: compressedFile,
        originalName,
        wasCompressed: true,
        originalSize,
        compressedSize: compressedFile.size,
      };
    }

    return {
      file: processedFile,
      originalName,
      wasCompressed: processedFile !== file,
      originalSize,
      compressedSize: processedFile.size,
    };
  } catch (error) {
    console.error("Image compression failed:", error);
    // Return the processed file (HEIC->JPEG converted) even if compression fails
    return {
      file: processedFile,
      originalName,
      wasCompressed: processedFile !== file,
      originalSize,
      compressedSize: processedFile.size,
    };
  }
}

/**
 * Compress multiple images in parallel with concurrency limit.
 * Returns an array of compression results.
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {},
  onProgress?: (completed: number, total: number) => void,
): Promise<CompressionResult[]> {
  const total = files.length;
  let completed = 0;

  const results: CompressionResult[] = [];

  // Process in batches of 3 to avoid overwhelming the browser
  const batchSize = 3;
  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(async (file) => {
        const result = await compressImage(file, options);
        completed++;
        onProgress?.(completed, total);
        return result;
      }),
    );
    results.push(...batchResults);
  }

  return results;
}

/**
 * Helper to format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Calculate total savings from compression
 */
export function calculateCompressionSavings(results: CompressionResult[]): {
  originalTotal: number;
  compressedTotal: number;
  savedBytes: number;
  savedPercentage: number;
} {
  const originalTotal = results.reduce((sum, r) => sum + r.originalSize, 0);
  const compressedTotal = results.reduce((sum, r) => sum + r.compressedSize, 0);
  const savedBytes = originalTotal - compressedTotal;
  const savedPercentage =
    originalTotal > 0 ? (savedBytes / originalTotal) * 100 : 0;

  return { originalTotal, compressedTotal, savedBytes, savedPercentage };
}
